"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { ChevronDown, LayoutGrid, UserPlus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import { useProjectStore } from "@/stores/useProjectStore";
import { useConversationStore } from "@/stores/useConversationStore";
import { cn } from "@/lib/utils";
import { ShareDialog } from "@/components/share-dialog";
import type { CNSWorkspace } from "@/models/cns";
interface NavWorkspaceSwitcherProps {
  mode?: "list" | "compact";
  showWorkspaceInvite?: boolean;
}
interface WorkspaceMenuItemsProps {
  workspaces: CNSWorkspace[];
  currentWorkspaceId: string | null;
  onSelectWorkspace: (workspaceId: string) => void;
  onInvite: () => void;
  showWorkspaceInvite?: boolean;
}
function WorkspaceMenuItems(t0) {
  const $ = _c(12);
  const {
    workspaces,
    currentWorkspaceId,
    onSelectWorkspace,
    onInvite,
    showWorkspaceInvite: t1
  } = t0;
  const showWorkspaceInvite = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== onSelectWorkspace || $[1] !== workspaces) {
    let t3;
    if ($[3] !== onSelectWorkspace) {
      t3 = workspace => <DropdownMenuItem key={workspace.id} onClick={() => onSelectWorkspace(workspace.id)}>{workspace.name}</DropdownMenuItem>;
      $[3] = onSelectWorkspace;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = workspaces.map(t3);
    $[0] = onSelectWorkspace;
    $[1] = workspaces;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== currentWorkspaceId || $[6] !== onInvite || $[7] !== showWorkspaceInvite) {
    t3 = showWorkspaceInvite && currentWorkspaceId ? <><DropdownMenuSeparator /><DropdownMenuItem onClick={onInvite}><UserPlus className="mr-2 size-4" />Invite to workspace</DropdownMenuItem></> : null;
    $[5] = currentWorkspaceId;
    $[6] = onInvite;
    $[7] = showWorkspaceInvite;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export function NavWorkspaceSwitcher(t0) {
  const $ = _c(63);
  const {
    mode: t1,
    showWorkspaceInvite: t2
  } = t0;
  const mode = t1 === undefined ? "list" : t1;
  const showWorkspaceInvite = t2 === undefined ? true : t2;
  const {
    state
  } = useSidebar();
  const isCollapsed = state === "collapsed";
  const {
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspace,
    fetchWorkspaces,
    loading
  } = useWorkspaceStore();
  const fetchProjects = useProjectStore(_temp);
  const invalidateForWorkspaceSwitch = useProjectStore(_temp2);
  const clearRecentConversations = useConversationStore(_temp3);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  let t3;
  let t4;
  if ($[0] !== fetchWorkspaces) {
    t3 = () => {
      fetchWorkspaces();
    };
    t4 = [fetchWorkspaces];
    $[0] = fetchWorkspaces;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  if (workspaces.length === 0 && !loading) {
    return null;
  }
  let t5;
  if ($[3] !== currentWorkspaceId || $[4] !== workspaces) {
    let t6;
    if ($[6] !== currentWorkspaceId) {
      t6 = w => w.id === currentWorkspaceId;
      $[6] = currentWorkspaceId;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = workspaces.find(t6);
    $[3] = currentWorkspaceId;
    $[4] = workspaces;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const currentWorkspace = t5;
  const workspaceLabel = currentWorkspace?.name ?? "Workspace";
  let t6;
  if ($[8] !== clearRecentConversations || $[9] !== currentWorkspaceId || $[10] !== fetchProjects || $[11] !== invalidateForWorkspaceSwitch || $[12] !== setCurrentWorkspace) {
    t6 = workspaceId => {
      if (workspaceId === currentWorkspaceId) {
        return;
      }
      setCurrentWorkspace(workspaceId);
      invalidateForWorkspaceSwitch();
      clearRecentConversations();
      fetchProjects();
    };
    $[8] = clearRecentConversations;
    $[9] = currentWorkspaceId;
    $[10] = fetchProjects;
    $[11] = invalidateForWorkspaceSwitch;
    $[12] = setCurrentWorkspace;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleSelectWorkspace = t6;
  let t7;
  if ($[14] !== currentWorkspace?.name || $[15] !== currentWorkspaceId || $[16] !== shareDialogOpen) {
    t7 = currentWorkspaceId ? <ShareDialog open={shareDialogOpen} onOpenChange={setShareDialogOpen} entityType="workspace" entityPublicId={currentWorkspaceId} entityLabel={currentWorkspace?.name} /> : null;
    $[14] = currentWorkspace?.name;
    $[15] = currentWorkspaceId;
    $[16] = shareDialogOpen;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const shareDialog = t7;
  if (mode === "compact") {
    const t8 = isCollapsed ? workspaceLabel : undefined;
    const t9 = isCollapsed ? "justify-center" : "justify-between";
    let t10;
    if ($[18] !== t9) {
      t10 = cn("w-full", t9);
      $[18] = t9;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== currentWorkspace?.isShared || $[21] !== isCollapsed || $[22] !== workspaceLabel) {
      t11 = isCollapsed ? <LayoutGrid className="size-4" aria-label={workspaceLabel} /> : <><span className="flex min-w-0 items-center gap-2"><LayoutGrid className="size-4 shrink-0" /><span className="truncate">{workspaceLabel}</span>{currentWorkspace?.isShared ? <Badge variant="shared" className="shrink-0 px-2 py-0 text-[10px] font-medium">Invited</Badge> : null}</span><ChevronDown className="size-4 shrink-0 opacity-60" /></>;
      $[20] = currentWorkspace?.isShared;
      $[21] = isCollapsed;
      $[22] = workspaceLabel;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== t10 || $[25] !== t11 || $[26] !== t8) {
      t12 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton tooltip={t8} className={t10}>{t11}</SidebarMenuButton></DropdownMenuTrigger>;
      $[24] = t10;
      $[25] = t11;
      $[26] = t8;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = () => setShareDialogOpen(true);
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== currentWorkspaceId || $[30] !== handleSelectWorkspace || $[31] !== showWorkspaceInvite || $[32] !== workspaces) {
      t14 = <DropdownMenuContent align="start" className="w-56"><WorkspaceMenuItems workspaces={workspaces} currentWorkspaceId={currentWorkspaceId} onSelectWorkspace={handleSelectWorkspace} onInvite={t13} showWorkspaceInvite={showWorkspaceInvite} /></DropdownMenuContent>;
      $[29] = currentWorkspaceId;
      $[30] = handleSelectWorkspace;
      $[31] = showWorkspaceInvite;
      $[32] = workspaces;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== t12 || $[35] !== t14) {
      t15 = <SidebarGroupContent><SidebarMenu><SidebarMenuItem><DropdownMenu>{t12}{t14}</DropdownMenu></SidebarMenuItem></SidebarMenu></SidebarGroupContent>;
      $[34] = t12;
      $[35] = t14;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    let t16;
    if ($[37] !== shareDialog || $[38] !== t15) {
      t16 = <SidebarGroup className="py-0">{t15}{shareDialog}</SidebarGroup>;
      $[37] = shareDialog;
      $[38] = t15;
      $[39] = t16;
    } else {
      t16 = $[39];
    }
    return t16;
  }
  const t8 = isCollapsed && "px-0";
  let t9;
  if ($[40] !== t8) {
    t9 = cn(t8);
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  const t10 = !isCollapsed && "Workspace";
  let t11;
  if ($[42] !== t10 || $[43] !== t9) {
    t11 = <SidebarGroupLabel className={t9}>{t10}</SidebarGroupLabel>;
    $[42] = t10;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== currentWorkspaceId || $[46] !== handleSelectWorkspace || $[47] !== isCollapsed || $[48] !== loading || $[49] !== workspaces) {
    t12 = loading ? <SidebarMenuItem><SidebarMenuButton className={cn(isCollapsed && "justify-center")} disabled={true}>{!isCollapsed && <span className="text-sm text-muted-foreground">Loading...</span>}</SidebarMenuButton></SidebarMenuItem> : workspaces.map(workspace => {
      const isActive = workspace.id === currentWorkspaceId;
      return <SidebarMenuItem key={workspace.id}><SidebarMenuButton tooltip={workspace.name} isActive={isActive} className={cn(isCollapsed && "justify-center")} onClick={() => handleSelectWorkspace(workspace.id)}>{isCollapsed ? <LayoutGrid className="size-4" /> : <><LayoutGrid className="size-4" /><span className="truncate">{workspace.name}</span>{workspace.isShared ? <Badge variant="shared" className="shrink-0 px-2 py-0 text-[10px] font-medium">Invited</Badge> : null}</>}</SidebarMenuButton></SidebarMenuItem>;
    });
    $[45] = currentWorkspaceId;
    $[46] = handleSelectWorkspace;
    $[47] = isCollapsed;
    $[48] = loading;
    $[49] = workspaces;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== currentWorkspaceId || $[52] !== isCollapsed || $[53] !== loading || $[54] !== showWorkspaceInvite) {
    t13 = showWorkspaceInvite && currentWorkspaceId && !loading ? <SidebarMenuItem><SidebarMenuButton className={cn(isCollapsed && "justify-center")} onClick={() => setShareDialogOpen(true)} tooltip={isCollapsed ? "Invite to workspace" : undefined}>{isCollapsed ? <UserPlus className="size-4" /> : <><UserPlus className="size-4" /><span>Invite to workspace</span></>}</SidebarMenuButton></SidebarMenuItem> : null;
    $[51] = currentWorkspaceId;
    $[52] = isCollapsed;
    $[53] = loading;
    $[54] = showWorkspaceInvite;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== t12 || $[57] !== t13) {
    t14 = <SidebarGroupContent><SidebarMenu>{t12}{t13}</SidebarMenu></SidebarGroupContent>;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== shareDialog || $[60] !== t11 || $[61] !== t14) {
    t15 = <SidebarGroup className="py-0">{t11}{t14}{shareDialog}</SidebarGroup>;
    $[59] = shareDialog;
    $[60] = t11;
    $[61] = t14;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  return t15;
}
function _temp3(s_1) {
  return s_1.clearRecentConversations;
}
function _temp2(s_0) {
  return s_0.invalidateForWorkspaceSwitch;
}
function _temp(s) {
  return s.fetchProjects;
}